import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, ChevronRight, Search, ShieldCheck, X } from 'lucide-react';
import {
  customerRepository,
  orderItemRepository,
  orderRepository,
  paymentRepository,
} from '../../db/repositories';
import { releaseOrder, transitionOrderStatus } from '../../domain/orders/orderLifecycle';
import type { CustomerRecord, OrderItemRecord, OrderRecord, PaymentRecord } from '../../db/types';
import { PricingEngine } from '../../domain/pricing';
import { getReceiptAddOnLines } from '../../domain/receipts/receiptUtils';
import { AddOnSelectionList } from './AddOnSelectionList';

type Props = { refreshKey: number; onClose: () => void };

type Detail = {
  order: OrderRecord;
  customer?: CustomerRecord;
  items: OrderItemRecord[];
  payments: PaymentRecord[];
};

export function ClaimManagementDialog({ refreshKey, onClose }: Props) {
  const [query, setQuery] = useState('');
  const [orders, setOrders] = useState<OrderRecord[]>([]);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [confirmRelease, setConfirmRelease] = useState(false);
  const [error, setError] = useState('');

  const load = async () =>
    setOrders(
      query.trim() ? await orderRepository.search(query) : await orderRepository.listReady(),
    );
  useEffect(() => {
    void load();
  }, [query, refreshKey]);

  const loadDetail = async (order: OrderRecord) => {
    const [customer, items, payments] = await Promise.all([
      order.customerId ? customerRepository.getById(order.customerId) : undefined,
      orderItemRepository.listByOrderId(order.id),
      paymentRepository.listByOrderId(order.id),
    ]);
    setDetail({ order, customer, items, payments });
    setError('');
  };

  const setStatus = async (status: 'PROCESSING' | 'READY') => {
    if (!detail) return;
    try {
      await transitionOrderStatus({ orderId: detail.order.id, to: status });
      await load();
      const updated = await orderRepository.getById(detail.order.id);
      if (updated) await loadDetail(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to update order.');
    }
  };

  const release = async () => {
    if (!detail) return;
    try {
      await releaseOrder({ orderId: detail.order.id });
      setConfirmRelease(false);
      const updated = await orderRepository.getById(detail.order.id);
      if (updated) setDetail({ ...detail, order: updated });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to release order.');
    }
  };

  const resultLabel = useMemo(() => (query.trim() ? 'Search results' : 'Ready orders'), [query]);

  return (
    <div className="fixed inset-0 z-[75] grid place-items-center bg-slate-950/40 p-5">
      <div className="flex max-h-[92dvh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <header className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <p className="text-[10px] font-extrabold uppercase tracking-widest text-[#00549C]">
              Claim management
            </p>
            <h2 className="text-2xl font-black text-[#003B70]">Ready & Release Orders</h2>
          </div>
          <button
            onClick={onClose}
            className="grid h-11 w-11 place-items-center rounded-xl bg-slate-100"
            aria-label="Close"
          >
            <X />
          </button>
        </header>
        <div className="grid min-h-0 flex-1 gap-4 overflow-auto p-5 lg:grid-cols-[.9fr_1.1fr]">
          <section className="min-h-0 rounded-2xl border border-slate-200 bg-[#F7FAFC] p-4">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                size={18}
              />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Order #, claim code, customer, mobile"
                className="h-12 w-full rounded-xl border border-slate-200 bg-white pl-10 pr-3 font-semibold outline-none focus:border-[#00549C]"
              />
            </div>
            <div className="mt-4 flex items-center justify-between">
              <p className="text-xs font-black uppercase tracking-widest text-[#00549C]">
                {resultLabel}
              </p>
              <span className="text-xs font-bold text-slate-400">{orders.length}</span>
            </div>
            <div className="mt-2 space-y-2 overflow-auto lg:max-h-[58dvh]">
              {orders.map((order) => (
                <button
                  key={order.id}
                  onClick={() => void loadDetail(order)}
                  className={`w-full rounded-xl border p-3 text-left ${detail?.order.id === order.id ? 'border-[#00549C] bg-[#DCEEFF]' : 'border-slate-200 bg-white'}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-black text-[#003B70]">{order.orderNumber}</p>
                      <p className="text-xs font-bold text-slate-400">
                        Claim {order.claimCode} • {order.orderStatus}
                      </p>
                    </div>
                    <ChevronRight size={18} className="text-slate-400" />
                  </div>
                </button>
              ))}
              {orders.length === 0 && (
                <p className="p-8 text-center text-sm font-semibold text-slate-400">
                  No matching orders.
                </p>
              )}
            </div>
          </section>
          <section className="rounded-2xl border border-slate-200 bg-white p-5">
            {!detail ? (
              <div className="grid min-h-[420px] place-items-center text-center text-slate-400">
                <div>
                  <ShieldCheck size={44} className="mx-auto opacity-40" />
                  <p className="mt-3 font-black">Select an order to verify</p>
                  <p className="text-sm">
                    Customer, payment, items, and status will be shown before release.
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-[10px] font-extrabold uppercase tracking-widest text-[#00549C]">
                      Order verification
                    </p>
                    <h3 className="text-2xl font-black text-[#003B70]">
                      {detail.order.orderNumber}
                    </h3>
                    <p className="mt-1 text-sm font-bold text-slate-500">
                      Claim code{' '}
                      <span className="font-black text-[#00549C]">{detail.order.claimCode}</span>
                    </p>
                  </div>
                  <span
                    className={`rounded-full px-3 py-2 text-xs font-black ${detail.order.orderStatus === 'READY' ? 'bg-emerald-50 text-emerald-700' : detail.order.orderStatus === 'CLAIMED' ? 'bg-slate-100 text-slate-600' : 'bg-amber-50 text-amber-700'}`}
                  >
                    {detail.order.orderStatus}
                  </span>
                </div>
                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-xl bg-[#F7FAFC] p-4">
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                      Customer
                    </p>
                    <p className="mt-2 font-black text-slate-800">
                      {detail.customer?.name ?? 'Walk-in customer'}
                    </p>
                    <p className="text-sm font-semibold text-slate-500">
                      {detail.customer?.mobileNumber ?? 'No mobile number'}
                    </p>
                  </div>
                  <div className="rounded-xl bg-[#F7FAFC] p-4">
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                      Payment
                    </p>
                    <p className="mt-2 font-black text-slate-800">{detail.order.paymentStatus}</p>
                    <p className="text-sm font-semibold text-slate-500">
                      {detail.payments
                        .map((p) => `${p.method} ${PricingEngine.formatCentavos(p.amountCentavos)}`)
                        .join(' • ') || 'No payment record'}
                    </p>
                  </div>
                </div>
                <div className="mt-3 rounded-xl border border-slate-100 p-4">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                    Items
                  </p>
                  <div className="mt-2 space-y-2">
                    {detail.items.map((item) => (
                      <div key={item.id} className="flex justify-between gap-3 text-sm">
                        <span className="font-bold text-slate-700">
                          {item.serviceItemName
                            ? `${item.serviceItemName} × ${item.quantity}`
                            : item.serviceName}
                          {item.weightKg ? ` • ${item.weightKg} KG` : ''}
                        </span>
                        <span className="font-black text-[#003B70]">
                          {PricingEngine.formatCentavos(item.lineTotalCentavos)}
                        </span>
                      </div>
                    ))}
                    <AddOnSelectionList
                      items={getReceiptAddOnLines(detail.items)}
                      label="Order add-ons"
                    />
                  </div>
                  <div className="mt-3 flex justify-between border-t border-slate-100 pt-3">
                    <span className="font-black text-slate-500">Total</span>
                    <span className="text-xl font-black text-[#003B70]">
                      {PricingEngine.formatCentavos(detail.order.totalCentavos)}
                    </span>
                  </div>
                </div>
                {error && (
                  <p className="mt-3 rounded-xl bg-red-50 p-3 text-sm font-bold text-red-600">
                    {error}
                  </p>
                )}
                <div className="mt-4 flex flex-wrap gap-2">
                  {detail.order.orderStatus === 'RECEIVED' && (
                    <button
                      onClick={() => void setStatus('PROCESSING')}
                      className="min-h-[56px] flex-1 rounded-xl border border-[#9EC6E9] bg-[#DCEEFF] px-4 font-black text-[#00549C]"
                    >
                      MARK PROCESSING
                    </button>
                  )}
                  {detail.order.orderStatus === 'PROCESSING' && (
                    <button
                      onClick={() => void setStatus('READY')}
                      className="min-h-[56px] flex-1 rounded-xl bg-emerald-600 px-4 font-black text-white"
                    >
                      MARK READY
                    </button>
                  )}
                  {detail.order.orderStatus === 'READY' && (
                    <button
                      onClick={() => setConfirmRelease(true)}
                      className="min-h-[64px] w-full rounded-xl bg-[#00549C] px-4 text-lg font-black text-white"
                    >
                      RELEASE ORDER
                    </button>
                  )}
                </div>
              </>
            )}
          </section>
        </div>
        {confirmRelease && detail && (
          <div className="absolute inset-0 z-10 grid place-items-center bg-slate-950/40 p-5">
            <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
              <CheckCircle2 size={42} className="text-emerald-600" />
              <h3 className="mt-3 text-xl font-black text-[#003B70]">Confirm order release</h3>
              <p className="mt-2 text-sm font-semibold leading-6 text-slate-600">
                Release <strong>{detail.order.orderNumber}</strong> for{' '}
                <strong>{detail.customer?.name ?? 'walk-in customer'}</strong>? This will
                permanently mark the order as CLAIMED on this device.
              </p>
              <div className="mt-5 grid grid-cols-2 gap-3">
                <button
                  onClick={() => setConfirmRelease(false)}
                  className="min-h-[56px] rounded-xl border border-slate-200 font-black text-slate-600"
                >
                  CANCEL
                </button>
                <button
                  onClick={() => void release()}
                  className="min-h-[56px] rounded-xl bg-[#00549C] font-black text-white"
                >
                  CONFIRM RELEASE
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
